import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth, db, storage } from '../firebase';
import { collection, query, where, getDocs, orderBy, limit, doc, onSnapshot, addDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { getDownloadURL, ref as storageRef } from 'firebase/storage';
import { FaUpload, FaCog } from 'react-icons/fa';
import { useUploadStatus } from '../contexts/UploadStatusContext';
import { useSidebar } from '../contexts/SidebarContext';
import RunCard from './RunCard';
import { cacheKey, getCached, getCachedStale, setCached, fingerprint } from '../utils/pageCache';
import { RUN_CACHE_PATCHED_EVENT, salvageCachedRunsNotInFetch } from '../utils/runListCache';
import {
  enqueuePendingRunInline,
  flushPendingRuns as flushPendingRunsUtil,
  listPendingRunsForDisplay,
  mergeFirestoreRunsWithPending,
} from '../utils/pendingRunsQueue';
import { fetchOwnRunsForUserFirestore, sortRunsByActivityDesc } from '../utils/fetchOwnRunsFromFirestore';
import {
  downloadDataloggerCsvsByName,
  decodeShokiqBinaryToCsv,
  isDataloggerDevProxyActive,
  listDataloggerCsvFilenames,
  pingDatalogger,
  resolveDataloggerOrigin,
} from '../utils/dataloggerSync';
import './Home.css';
const ESP_URL_KEY = 'homeEspUrl_v1';
const FIRESTORE_WRITE_TIMEOUT_MS = 12000;

function getValidNumber(v) {
  const n = typeof v === 'string' ? Number(v) : v;
  return typeof n === 'number' && Number.isFinite(n) ? n : null;
}

function formatByteSize(n) {
  const x = typeof n === 'number' && Number.isFinite(n) ? n : 0;
  if (x < 1024) return `${Math.round(x)} B`;
  if (x < 1024 * 1024) return `${(x / 1024).toFixed(1)} KB`;
  return `${(x / (1024 * 1024)).toFixed(1)} MB`;
}

function formatUploadDate(value) {
  if (!value) return '';
  const date = typeof value.toDate === 'function' ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString();
}

function isCsvFile(file) {
  if (!file) return false;
  const name = String(file.name || '').toLowerCase();
  const type = String(file.type || '').toLowerCase();
  return name.endsWith('.csv') || name.endsWith('.shq') || type === 'text/csv' || type === 'application/vnd.ms-excel';
}

function isBybFile(file) {
  if (!file) return false;
  const name = String(file.name || '').toLowerCase();
  return name.endsWith('.byb');
}

/** CSV or BrakeAce/BYB binary — same navigate-to-create path as CSV-only uploads. */
function isRideCreateTelemetryFile(file) {
  return isCsvFile(file) || isBybFile(file);
}

function parseCsvTelemetryText(text) {
  if (!text || typeof text !== 'string') return [];
  let delimiter = ',';
  const commaCount = text.split(',').length;
  const semicolonCount = text.split(';').length;
  if (semicolonCount > commaCount) delimiter = ';';
  const lines = text.trim().split('\n');
  if (lines.length < 2) return [];

  const headers = lines[0].split(delimiter).map((h) => h.trim());
  const headerMap = {
    'Time (s)': 'time', Time: 'time', time: 'time', Elapsed: 'time', Seconds: 'time', Sec: 'time', timestamp_ms: 'time',
    'Distance (m)': 'distance', Distance: 'distance', Dist: 'distance',
    'Latitude (deg)': 'lat', Latitude: 'lat', latitude: 'lat', Lat: 'lat', gps_lat: 'lat',
    'Longitude (deg)': 'lon', Longitude: 'lon', longitude: 'lon', Lon: 'lon', gps_long: 'lon',
    'Elevation (m)': 'elevation', Elevation: 'elevation', elevation_m: 'elevation', Altitude: 'elevation', Ele: 'elevation',
    'Speed (km/h)': 'speed', Speed: 'speed', speed_m_s: 'speed', gps_speed: 'speed', gpsSpeed: 'speed',
    'Front Brake Power (W)': 'front_brake', Fbrake: 'front_brake', 'Front Brake': 'front_brake',
    'Rear Brake Power (W)': 'rear_brake', Rbrake: 'rear_brake', 'Rear Brake': 'rear_brake',
    'Heart Rate': 'heartRate', HR: 'heartRate', heart_rate: 'heartRate',
    'Power (W)': 'power', Power: 'power',
    'Cadence (rpm)': 'cadence', Cadence: 'cadence',
    Fork: 'fork', Shock: 'shock', front_disp: 'fork', rear_disp: 'shock',
  };

  const colMap = {};
  headers.forEach((header, index) => {
    const key = headerMap[header] || header.replace(/\s+/g, '_').replace(/[^a-z0-9_]/gi, '').toLowerCase();
    if (key) colMap[key] = index;
  });
  const timeIdx = headers.findIndex((h) => /^time|elapsed|sec(onds)?$/i.test(h.trim()));
  const timeHeader = timeIdx >= 0 ? headers[timeIdx] : null;
  const speedIdx = headers.findIndex((h) => headerMap[h] === 'speed');
  const speedHeader = speedIdx >= 0 ? headers[speedIdx] : null;

  const data = [];
  let lastTime = 0;
  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(delimiter);
    const row = {};
    for (const [key, idx] of Object.entries(colMap)) {
      const v = values[idx]?.trim();
      const num = parseFloat(v);
      if (v !== undefined && v !== '' && !Number.isNaN(num)) row[key] = num;
    }
    let t = row.time;
    if (typeof t !== 'number' || !Number.isFinite(t)) t = timeIdx >= 0 ? parseFloat(values[timeIdx]) : Number.NaN;
    if (timeHeader === 'timestamp_ms' && Number.isFinite(t)) t = t / 1000;
    if (!Number.isFinite(t)) t = lastTime + (i === 1 ? 0 : 1);
    row.time = t;
    lastTime = t;
    if (speedHeader === 'speed_m_s' && typeof row.speed === 'number') row.speed *= 3.6;
    const hasMetric = Object.keys(row).some((k) => k !== 'time' && typeof row[k] === 'number' && Number.isFinite(row[k]));
    if (hasMetric) data.push(row);
  }
  data.sort((a, b) => a.time - b.time);
  return data;
}

const Home = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = auth.currentUser;
  const uploadStatus = useUploadStatus();
  const { isCollapsed } = useSidebar();
  const [stats, setStats] = useState({
    totalRides: 0,
    totalDistance: 0,
    recentRides: [],
  });
  const [loading, setLoading] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  /** Bumps on each loadUserStats call so slower, older fetches cannot overwrite newer results (e.g. after datalogger import). */
  const homeStatsFetchGenRef = useRef(0);
  const [publicGroups, setPublicGroups] = useState([]);
  const [userData, setUserData] = useState(null);
  const [espUrl, setEspUrl] = useState(() => {
    try {
      return localStorage.getItem(ESP_URL_KEY) || 'http://192.168.4.1';
    } catch (_err) {
      return 'http://192.168.4.1';
    }
  });
  const [espDraftUrl, setEspDraftUrl] = useState('');
  const [showEspSettings, setShowEspSettings] = useState(false);
  const [uploadState, setUploadState] = useState({ type: 'info', text: 'Ready to sync with datalogger.', progress: 0, done: false });
  const [uploadingCsv, setUploadingCsv] = useState(false);
  const [cloudLoggerUploads, setCloudLoggerUploads] = useState([]);
  const [cloudLoggerBusy, setCloudLoggerBusy] = useState(false);
  const [dataloggerNet, setDataloggerNet] = useState(() => {
    try {
      const raw = localStorage.getItem(ESP_URL_KEY) || 'http://192.168.4.1';
      const o = resolveDataloggerOrigin(raw);
      return { status: 'unknown', label: o ? o.replace(/^https?:\/\//i, '') : '' };
    } catch (_e) {
      return { status: 'unknown', label: '192.168.4.1' };
    }
  });
  const [dataloggerProbeKey, setDataloggerProbeKey] = useState(0);
  const [dataloggerPicker, setDataloggerPicker] = useState({ open: false, fileNames: [], selected: {} });

  useEffect(() => {
    if (!user) {
      setUserData(null);
      return;
    }
    const docRef = doc(db, 'users', user.uid);
    const unsubscribe = onSnapshot(docRef, (snapshot) => {
      if (snapshot.exists()) {
        setUserData(snapshot.data());
      }
    });
    return () => unsubscribe();
  }, [user]);

  const welcomeName = (() => {
    const raw = userData?.username || user?.displayName || (user?.email ? user.email.split('@')[0] : null) || 'Rider';
    return (raw || '').charAt(0).toUpperCase() + (raw || '').slice(1).toLowerCase();
  })();

  const publicGroupsByBrand = useMemo(() => {
    const byBrand = new Map();
    for (const g of publicGroups) {
      const brand = (g.brand || 'Other').trim() || 'Other';
      if (!byBrand.has(brand)) byBrand.set(brand, []);
      byBrand.get(brand).push(g);
    }
    return Array.from(byBrand.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [publicGroups]);

  useEffect(() => {
    if (!user) {
      const key = cacheKey('home', 'anon');
      const cached = getCached(key);
      if (cached?.publicGroups?.length !== undefined) {
        setPublicGroups(cached.publicGroups);
        setLoading(false);
      } else {
        setLoading(true);
      }
      loadPublicGroups(key, cached);
      return;
    }
    const key = cacheKey('home', user.uid);
    const cachedFresh = getCached(key);
    const cachedStale = getCachedStale(key);
    const cached = cachedFresh?.stats ? cachedFresh : cachedStale;
    if (cached?.stats) {
      setStats(cached.stats);
      setLoading(false);
      listPendingRunsForDisplay(user.uid).then((pending) => {
        if (!pending.length) return;
        setStats((prev) => {
          const next = {
            ...prev,
            recentRides: mergeFirestoreRunsWithPending(prev.recentRides || [], pending).slice(0, 5),
          };
          try {
            const stale = getCachedStale(key) || {};
            setCached(key, { ...stale, stats: next });
          } catch (_e) {
            // ignore cache write errors (quota, private mode)
          }
          return next;
        });
      });
    } else {
      setLoading(true);
    }
    loadUserStats(key);
  }, [user, navigate, location.key]);

  useEffect(() => {
    if (!user) return undefined;
    const key = cacheKey('home', user.uid);
    const onCachePatched = (e) => {
      if (e.detail?.userId !== user.uid) return;
      const stale = getCachedStale(key);
      if (stale?.stats) setStats(stale.stats);
    };
    window.addEventListener(RUN_CACHE_PATCHED_EVENT, onCachePatched);
    return () => window.removeEventListener(RUN_CACHE_PATCHED_EVENT, onCachePatched);
  }, [user]);

  useEffect(() => {
    setEspDraftUrl(espUrl);
  }, [espUrl, showEspSettings]);

  const retestDataloggerConnection = useCallback(() => {
    const origin = resolveDataloggerOrigin(espUrl);
    const host = origin ? origin.replace(/^https?:\/\//i, '') : '';
    setDataloggerNet((prev) => ({ ...prev, status: 'unknown', label: host || prev.label }));
    setDataloggerProbeKey((k) => k + 1);
  }, [espUrl]);

  useEffect(() => {
    if (!user) return undefined;
    const origin = resolveDataloggerOrigin(espUrl);
    if (!origin) {
      setDataloggerNet({ status: 'unknown', label: '', viaProxy: false });
      return undefined;
    }
    const host = origin.replace(/^https?:\/\//i, '');

    /** While a CSV sync runs, skip reachability pings — the logger often stops answering / briefly during SD reads. */
    if (uploadingCsv) {
      setDataloggerNet({
        status: 'syncing',
        label: host,
        viaProxy: isDataloggerDevProxyActive(espUrl),
      });
      return undefined;
    }

    let cancelled = false;
    async function tick() {
      const ok = await pingDatalogger(origin, { timeoutMs: 3500 });
      if (cancelled) return;
      setDataloggerNet({
        status: ok ? 'reachable' : 'unreachable',
        label: host,
        viaProxy: isDataloggerDevProxyActive(espUrl),
      });
    }
    tick();
    const id = setInterval(tick, 20000);
    const onVis = () => {
      if (document.visibilityState === 'visible') tick();
    };
    window.addEventListener('online', tick);
    window.addEventListener('focus', tick);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener('online', tick);
      window.removeEventListener('focus', tick);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [user, espUrl, dataloggerProbeKey, uploadingCsv]);

  const enqueuePendingRun = useCallback((runData) => {
    try {
      enqueuePendingRunInline(runData);
    } catch (err) {
      console.error('Failed to queue run locally:', err);
    }
  }, []);

  const flushPendingRuns = useCallback(async () => {
    return flushPendingRunsUtil(db, user);
  }, [user]);

  useEffect(() => {
    if (!user) return undefined;
    flushPendingRuns().catch((err) => console.error('Initial pending run sync failed:', err));
    const onOnline = () => {
      flushPendingRuns()
        .then((count) => {
          if (count > 0) {
            setUploadState({ type: 'success', text: `${count} offline run(s) synced.` });
            loadUserStats(cacheKey('home', user.uid));
          }
        })
        .catch((err) => console.error('Online pending run sync failed:', err));
    };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [user, flushPendingRuns]);

  const createRunFromTelemetry = useCallback(async (telemetryData, sourceLabel, options = {}) => {
    if (!user) throw new Error('You must be signed in.');
    const { preferOffline = false } = options;
    const now = Date.now();
    const runName = `${sourceLabel} ${new Date(now).toLocaleString()}`;
    const runData = {
      userId: user.uid,
      ownerId: user.uid,
      runName,
      name: runName,
      description: '',
      videoUrl: null,
      videoFileName: null,
      telemetryData,
      source: 'esp-csv',
      sourceFile: sourceLabel,
      timestamp: Timestamp.fromMillis(now),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    if (preferOffline) {
      enqueuePendingRun({
        ...runData,
        createdAt: now,
        updatedAt: now,
      });
      return { queued: true };
    }

    try {
      await Promise.race([
        addDoc(collection(db, 'runs'), runData),
        new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Firestore write timeout')), FIRESTORE_WRITE_TIMEOUT_MS);
        }),
      ]);
      return { queued: false };
    } catch (err) {
      enqueuePendingRun({
        ...runData,
        createdAt: now,
        updatedAt: now,
      });
      return { queued: true, error: err };
    }
  }, [enqueuePendingRun, user]);

  const createRunsFromCsvFiles = useCallback(async (csvFiles) => {
    if (!csvFiles?.length) return;
    const cache = cacheKey('home', user.uid);
    setUploadingCsv(true);
    setUploadState({ type: 'info', text: 'Creating runs from CSV...', progress: 10, done: false });
    let created = 0;
    let queued = 0;
    for (let i = 0; i < csvFiles.length; i += 1) {
      const file = csvFiles[i];
      const text = await file.text();
      const telemetryData = parseCsvTelemetryText(text);
      if (telemetryData.length === 0) {
        throw new Error(`No valid telemetry in ${file.name}`);
      }
      const result = await createRunFromTelemetry(telemetryData, file.name);
      if (result.queued) queued += 1;
      else created += 1;
      const doneCount = created + queued;
      const progress = Math.min(99, 60 + Math.round((doneCount / csvFiles.length) * 39));
      setUploadState({
        type: 'info',
        text: `${result.queued ? 'Queued offline' : 'Saved'} ${doneCount}/${csvFiles.length} (${progress}%) — ${file.name}`,
        progress,
        done: false,
      });
      await loadUserStats(cache);
    }
    setUploadingCsv(false);
    if (queued > 0 && created === 0) {
      setUploadState({ type: 'info', text: `${queued} run(s) saved locally. They will sync when online.`, progress: 100, done: true });
    } else {
      const queuedText = queued > 0 ? `, ${queued} queued offline` : '';
      setUploadState({ type: 'success', text: `${created} run(s) created${queuedText}.`, progress: 100, done: true });
    }
  }, [createRunFromTelemetry, user]);

  const importDataloggerEntries = useCallback(
    async (entries) => {
      const cache = cacheKey('home', user.uid);
      let created = 0;
      let queued = 0;
      let importIndex = 0;
      for (let idx = 0; idx < entries.length; idx += 1) {
        const { fileName, text } = entries[idx];
        const telemetryData = parseCsvTelemetryText(text);
        if (telemetryData.length === 0) continue;
        const result = await createRunFromTelemetry(telemetryData, fileName, { preferOffline: true });
        if (result.queued) queued += 1;
        else created += 1;
        importIndex += 1;
        const progress = Math.min(99, 60 + Math.round((importIndex / entries.length) * 39));
        setUploadState({
          type: 'info',
          text: `${result.queued ? 'Queued offline' : 'Imported'} ${importIndex}/${entries.length} (${progress}%) — ${fileName}`,
          progress,
          done: false,
        });
        await loadUserStats(cache);
      }
      if (created === 0 && queued === 0) {
        setUploadingCsv(false);
        setUploadState({ type: 'error', text: 'No usable telemetry in the selected file(s).', progress: 0, done: true });
        return;
      }
      setUploadingCsv(false);
      if (queued > 0 && created === 0) {
        setUploadState({
          type: 'info',
          text: `${queued} run(s) saved locally. They will sync when online.`,
          progress: 100,
          done: true,
        });
      } else {
        const queuedText = queued > 0 ? `, ${queued} queued offline` : '';
        setUploadState({
          type: 'success',
          text: `${created} run(s) created from ${entries.length} file(s)${queuedText}.`,
          progress: 100,
          done: true,
        });
      }
    },
    [createRunFromTelemetry, user]
  );

  const runDataloggerImportWithNames = useCallback(
    async (fileNames) => {
      const origin = resolveDataloggerOrigin(espUrl);
      if (!origin) {
        setUploadingCsv(false);
        setUploadState({ type: 'error', text: 'Invalid datalogger URL in settings.', progress: 0, done: true });
        return;
      }
      setUploadingCsv(true);
      setUploadState({
        type: 'info',
        text: 'Starting download from logger…',
        progress: 10,
        done: false,
      });
      const entries = await downloadDataloggerCsvsByName(
        origin,
        fileNames,
        parseCsvTelemetryText,
        ({ current, total, fileName, ok, phase, receivedBytes, totalBytes }) => {
          const t = Math.max(1, total);
          const slot = 1 / t;
          let fracInSlot = 0;
          if (phase === 'fetching') fracInSlot = 0;
          else if (phase === 'receiving' && totalBytes && totalBytes > 0 && typeof receivedBytes === 'number') {
            fracInSlot = Math.min(1, receivedBytes / totalBytes);
          } else if (phase === 'receiving') {
            fracInSlot = 0.08;
          } else if (ok === true) {
            fracInSlot = 1;
          } else if (ok === false) {
            fracInSlot = 1;
          }
          const overall = (current - 1) / t + fracInSlot * slot;
          const progress = Math.min(60, 5 + Math.round(overall * 55));

          let text;
          if (phase === 'fetching') {
            text = `Downloading ${current}/${total} (${progress}%) — connecting for ${fileName}…`;
          } else if (phase === 'receiving') {
            const rb = typeof receivedBytes === 'number' ? receivedBytes : 0;
            if (totalBytes && totalBytes > 0) {
              text = `Downloading ${current}/${total} (${progress}%) — ${fileName}: ${formatByteSize(rb)} / ${formatByteSize(totalBytes)}`;
            } else {
              text = `Downloading ${current}/${total} (${progress}%) — ${fileName}: ${formatByteSize(rb)} received…`;
            }
          } else if (ok) {
            text = `Downloaded ${current}/${total} (${progress}%) — ${fileName}`;
          } else {
            text = `Skipped ${current}/${total} (${progress}%): ${fileName} (missing or empty)`;
          }
          setUploadState({ type: 'info', text, progress, done: false });
        }
      );
      if (!entries.length) {
        setUploadingCsv(false);
        setUploadState({
          type: 'error',
          text: 'Download failed or no valid telemetry in the selected files. Check Wi‑Fi and the base URL in settings.',
          progress: 0,
          done: true,
        });
        return;
      }
      await importDataloggerEntries(entries);
    },
    [espUrl, importDataloggerEntries]
  );

  const handleDataloggerPickerImport = useCallback(async () => {
    const { fileNames, selected } = dataloggerPicker;
    const toImport = fileNames.filter((f) => selected[f]);
    if (toImport.length === 0) {
      return;
    }
    setDataloggerPicker({ open: false, fileNames: [], selected: {} });
    try {
      await runDataloggerImportWithNames(toImport);
    } catch (err) {
      console.error('Datalogger import failed:', err);
      setUploadingCsv(false);
      setUploadState({
        type: 'error',
        text: err?.message || 'Import failed. Check the connection and try again.',
        progress: 0,
        done: true,
      });
    }
  }, [dataloggerPicker, runDataloggerImportWithNames]);

  const closeDataloggerPicker = useCallback(() => {
    setDataloggerPicker({ open: false, fileNames: [], selected: {} });
  }, []);

  const toggleDataloggerFile = useCallback((name) => {
    setDataloggerPicker((prev) => ({
      ...prev,
      selected: { ...prev.selected, [name]: !prev.selected[name] },
    }));
  }, []);

  const setDataloggerAllSelected = useCallback((on) => {
    setDataloggerPicker((prev) => {
      const selected = {};
      prev.fileNames.forEach((f) => {
        selected[f] = on;
      });
      return { ...prev, selected };
    });
  }, []);

  const loadCloudLoggerUploads = useCallback(async () => {
    if (!user?.uid) {
      setCloudLoggerUploads([]);
      return;
    }
    try {
      const base = collection(db, 'loggerUploads');
      let snap;
      try {
        snap = await getDocs(query(base, where('userId', '==', user.uid), orderBy('createdAt', 'desc'), limit(8)));
      } catch (err) {
        console.warn('loggerUploads ordered query failed, using fallback:', err);
        snap = await getDocs(query(base, where('userId', '==', user.uid), limit(8)));
      }
      const uploads = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => {
          const ta = a.createdAt?.toMillis?.() || 0;
          const tb = b.createdAt?.toMillis?.() || 0;
          return tb - ta;
        });
      setCloudLoggerUploads(uploads);
    } catch (err) {
      console.error('Failed to load cloud logger uploads:', err);
      setCloudLoggerUploads([]);
    }
  }, [user]);

  useEffect(() => {
    loadCloudLoggerUploads();
  }, [loadCloudLoggerUploads]);

  const importCloudLoggerUpload = useCallback(async (upload) => {
    if (!user?.uid || !upload?.storagePath) return;
    setCloudLoggerBusy(true);
    setUploadState({
      type: 'info',
      text: `Importing uploaded logger file ${upload.fileName || upload.id}…`,
      progress: 20,
      done: false,
    });
    try {
      const url = await getDownloadURL(storageRef(storage, upload.storagePath));
      const resp = await fetch(url, { cache: 'no-store' });
      if (!resp.ok) throw new Error(`Download failed HTTP ${resp.status}`);

      let csvText = '';
      const fileName = upload.fileName || 'logger-upload.shq';
      if (String(fileName).toLowerCase().endsWith('.shq')) {
        const buffer = await resp.arrayBuffer();
        csvText = decodeShokiqBinaryToCsv(buffer);
      } else {
        csvText = await resp.text();
      }
      const telemetryData = parseCsvTelemetryText(csvText);
      if (!telemetryData.length) throw new Error('No usable telemetry found in uploaded file');

      const result = await createRunFromTelemetry(telemetryData, fileName);
      await loadUserStats(cacheKey('home', user.uid));
      setUploadState({
        type: result.queued ? 'info' : 'success',
        text: result.queued
          ? `Imported ${fileName} locally. It will sync when online.`
          : `Imported ${fileName} as a run.`,
        progress: 100,
        done: true,
      });
    } catch (err) {
      console.error('Cloud logger import failed:', err);
      setUploadState({
        type: 'error',
        text: err?.message || 'Could not import uploaded logger file.',
        progress: 0,
        done: true,
      });
    } finally {
      setCloudLoggerBusy(false);
    }
  }, [createRunFromTelemetry, user]);

  const handleFetchFromEsp = useCallback(async () => {
    if (!user) {
      setUploadState({ type: 'error', text: 'Please sign in to create and save runs.', progress: 0, done: true });
      return;
    }
    setUploadingCsv(true);
    setUploadState({ type: 'info', text: 'Contacting datalogger…', progress: 5, done: false });

    const origin = resolveDataloggerOrigin(espUrl);
    if (!origin) {
      setUploadingCsv(false);
      setUploadState({ type: 'error', text: 'Invalid datalogger URL. Use e.g. http://192.168.4.1', progress: 0, done: true });
      return;
    }

    try {
      setUploadState({ type: 'info', text: 'Loading file list (API, web page, or built‑in names)…', progress: 12, done: false });
      const names = await listDataloggerCsvFilenames(origin);
      if (!names.length) {
        throw new Error('NO_FILES');
      }
      const selected = {};
      names.forEach((n) => {
        selected[n] = true;
      });
      setDataloggerPicker({ open: true, fileNames: names, selected });
      setUploadingCsv(false);
      setUploadState({
        type: 'info',
        text: `Found ${names.length} CSV file(s) on the logger. Select which to import, then click Import selected.`,
        progress: 0,
        done: true,
      });
    } catch (err) {
      setUploadingCsv(false);
      const code = err && err.message;
      const failedFetch =
        err?.name === 'TypeError' || /failed to fetch/i.test(String(err?.message || ''));
      let text =
        'Failed: cannot reach the datalogger or no CSV list found. Connect to its Wi‑Fi and set the base URL in settings (http://192.168.4.1).';
      if (code === 'NO_FILES') {
        text =
          'No CSVs listed. Confirm the base URL, stay on the logger Wi‑Fi, and restart npm start so the /datalogger dev proxy is active. You can also use Browse Files to upload CSVs.';
      } else if (failedFetch && isDataloggerDevProxyActive(espUrl)) {
        text =
          'Could not reach the datalogger (dev proxy). Join the logger Wi‑Fi, keep npm start running, and use Test connection.';
      } else if (failedFetch) {
        text =
          'Network or CORS error. For hosted HTTPS, the logger may need CORS headers, or use npm start with the local proxy.';
      }
      setUploadState({ type: 'error', text, progress: 0, done: true });
      console.error('Datalogger list failed:', err);
    }
  }, [espUrl, user]);

  const saveEspSettings = useCallback(() => {
    const next = (espDraftUrl || '').trim();
    if (!next) return;
    setEspUrl(next);
    try {
      localStorage.setItem(ESP_URL_KEY, next);
    } catch (_err) {
      // no-op
    }
    setShowEspSettings(false);
  }, [espDraftUrl]);

  const loadPublicGroups = async (pageCacheKey, initialCached) => {
    try {
      if (!pageCacheKey) setLoading(true);
      let groupsQuery;
      try {
        groupsQuery = query(
          collection(db, 'groups'),
          where('visibility', '==', 'public'),
          orderBy('updatedAt', 'desc'),
          limit(30)
        );
      } catch (_err) {
        groupsQuery = query(
          collection(db, 'groups'),
          where('visibility', '==', 'public'),
          limit(30)
        );
      }
      const snap = await getDocs(groupsQuery);
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const payload = { publicGroups: data };
      if (pageCacheKey) {
        setCached(pageCacheKey, payload);
        const prevFp = initialCached?.publicGroups ? fingerprint(initialCached.publicGroups) : '';
        if (fingerprint(data) !== prevFp) setPublicGroups(data);
      } else {
        setPublicGroups(data);
      }
    } catch (error) {
      console.error('Error loading public groups:', error);
      setPublicGroups([]);
    } finally {
      setLoading(false);
    }
  };

  const loadUserStats = async (pageCacheKey) => {
    if (!user) return;

    const gen = (homeStatsFetchGenRef.current += 1);

    try {
      if (!pageCacheKey) setLoading(true);

      const [pendingRuns, runsFromFirestore] = await Promise.all([
        listPendingRunsForDisplay(user.uid),
        fetchOwnRunsForUserFirestore(db, user.uid, { timeoutMs: 18000 }).catch((err) => {
          console.error('Home: runs query failed:', err);
          return [];
        }),
      ]);

      if (gen !== homeStatsFetchGenRef.current) return;

      let runsData = mergeFirestoreRunsWithPending([...runsFromFirestore], pendingRuns);
      const prevRecentForSalvage = pageCacheKey
        ? getCachedStale(pageCacheKey)?.stats?.recentRides ?? []
        : [];
      runsData = salvageCachedRunsNotInFetch(prevRecentForSalvage, runsData);

      const totalRides = runsData.length;
      const totalDistanceMeters = runsData.reduce((sum, run) => {
        const directMeters = getValidNumber(run?.distance ?? run?.strava?.distanceMeters);
        if (directMeters !== null) return sum + directMeters;
        return sum;
      }, 0);

      // Same ordering / fetch path as My Rides (fetchOwnRunsFromFirestore + date-desc).
      const recentRides = sortRunsByActivityDesc(runsData).slice(0, 5);

      const nextStats = {
        totalRides,
        totalDistance: (totalDistanceMeters / 1000).toFixed(2),
        recentRides,
      };

      const payload = { stats: nextStats, runsForChart: [] };
      if (gen !== homeStatsFetchGenRef.current) return;
      if (pageCacheKey) {
        setCached(pageCacheKey, payload);
        setStats(nextStats);
      } else {
        setStats(nextStats);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      if (gen === homeStatsFetchGenRef.current) {
        setLoading(false);
      }
    }
  };

  const handleFilesUpload = useCallback(async (files) => {
    if (!user) {
      setUploadState({ type: 'error', text: 'Please sign in to create and save runs.', progress: 0, done: true });
      return;
    }
    const telemetryOnly = files.filter(isRideCreateTelemetryFile);
    if (telemetryOnly.length > 0 && telemetryOnly.length === files.length) {
      // Match RideView creation flow: open create page with selected telemetry files.
      navigate('/ride/create', { state: { files: telemetryOnly } });
      return;
    }

    const videoFile = files.find((file) => file.type.startsWith('video/'));

    if (videoFile) {
      const startTime = Date.now();
      uploadStatus.startGoproExtraction('extracting');

      navigate('/ride/create', { state: { files } });

      try {
        uploadStatus.updateGoproExtraction(5);
        const { extractGoProGPS, validateGPSData } = await import('../utils/goproExtractor');

        const extractionResult = await extractGoProGPS(
          videoFile,
          {
            requireGpsFix: false,
            debug: false,
            maxRetries: 3,
          },
          (progress) => {
            uploadStatus.updateGoproExtraction(progress);
            const elapsed = (Date.now() - startTime) / 1000;
            const fileSizeMB = videoFile.size / 1024 / 1024;
            const estimatedTotal = Math.max(10, fileSizeMB * 0.5);
            const remaining = Math.max(0, estimatedTotal - elapsed);
            uploadStatus.updateGoproExtraction(progress, remaining);
          }
        );

        uploadStatus.updateGoproExtraction(90);

        if (extractionResult.success) {
          const validation = validateGPSData(extractionResult.data);
          if (validation.valid) {
            uploadStatus.updateGoproExtraction(100);
          }
        }

        uploadStatus.completeGoproExtraction();
      } catch (error) {
        console.error('Error extracting GoPro GPS:', error);
        uploadStatus.completeGoproExtraction();
      }
    } else {
      navigate('/ride/create', { state: { files } });
    }
  }, [navigate, uploadStatus, user]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setIsDragging(false);
      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        handleFilesUpload(files);
      }
    },
    [handleFilesUpload]
  );

  const handleFileInput = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      handleFilesUpload(files);
    }
    e.target.value = '';
  };

  if (loading) {
    return (
      <div className="home-container content-full-flow">
        <div className="home-header">
          <div className="page-title-with-spinner">
            <h1>Welcome back, {welcomeName}!</h1>
            <span className="page-title-spinner" aria-hidden="true" />
          </div>
          <p className="home-subtitle">Loading your dashboard…</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="home-container content-full-flow">
        <div className="home-header">
          <h1>Public Content</h1>
          <p className="home-subtitle">
            Sign in to see your personal dashboard. Public groups and runs will be shown here.
          </p>
          <div className="home-public-actions">
            <button type="button" className="auth-btn filled" onClick={() => navigate('/login')}>
              Sign in
            </button>
            <button type="button" className="auth-btn outlined" onClick={() => navigate('/')}>
              Back to landing
            </button>
          </div>
        </div>

        {publicGroups.length === 0 ? (
          <div style={{ marginTop: 16, color: '#888' }}>
            No public groups yet.
          </div>
        ) : (
          <div style={{ marginTop: 16 }}>
            {publicGroupsByBrand.map(([brand, groups]) => (
              <div key={brand} style={{ marginBottom: 18 }}>
                <h2 style={{ marginBottom: 10 }}>{brand}</h2>
                <div className="recent-rides-grid">
                  {groups.map((g) => (
                    <div
                      key={g.id}
                      className="recent-ride-card"
                      onClick={() => navigate(`/g/${g.publicId}`)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') navigate(`/g/${g.publicId}`);
                      }}
                    >
                      <div className="ride-info">
                        <h4>{g.name || 'Untitled Group'}</h4>
                        <p className="ride-date">{g.location || 'No location'}</p>
                      </div>
                      <div className="ride-stats-mini">
                        <span>View</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="home-container content-full-flow">
      <div className="home-header">
        <h1>Welcome back, {welcomeName}!</h1>
      </div>

      <div className="upload-section">
        <div className="upload-header-row">
          <h2>Upload New Run</h2>
        </div>
        <div className="esp-net-row">
          <div className={`esp-net-pill esp-net-${dataloggerNet.status}`} role="status" aria-live="polite">
            <span className="esp-net-dot" aria-hidden="true" />
            <div className="esp-net-pill-body">
              {dataloggerNet.status === 'reachable' ? (
                <>
                  <span>{`Datalogger reachable at ${dataloggerNet.label}`}</span>
                  {dataloggerNet.viaProxy ? (
                    <span className="esp-net-proxy-note">Using dev server proxy (avoids CORS to the device).</span>
                  ) : null}
                </>
              ) : dataloggerNet.status === 'syncing' ? (
                <>
                  <span>{`Transfer from ${dataloggerNet.label} in progress`}</span>
                  <span className="esp-net-proxy-note">
                    Connection test is paused while files download — the device can look offline when busy. No public internet on
                    logger Wi‑Fi is expected.
                  </span>
                </>
              ) : dataloggerNet.status === 'unreachable' ? (
                <>
                  <span>{`No response from ${dataloggerNet.label || 'datalogger'} — join the logger Wi‑Fi or confirm the URL in settings.`}</span>
                  <span className="esp-net-proxy-note">
                    After switching Wi‑Fi, press Test connection. On the logger network you may have no public internet — that is fine. You can import CSVs with Browse Files; runs save offline and sync when you are online again.
                  </span>
                </>
              ) : (
                <span>Checking whether the datalogger responds…</span>
              )}
            </div>
          </div>
          <button type="button" className="esp-net-retest" onClick={retestDataloggerConnection}>
            Test connection
          </button>
        </div>
        <div className={`upload-feedback ${uploadState.type || 'info'}`}>
          <div className="upload-feedback-top">
            <div>{uploadState.text}</div>
            <div className="esp-status-header">
              <button
                type="button"
                className="esp-settings-button"
                onClick={() => setShowEspSettings(true)}
                title="ESP settings"
              >
                <FaCog />
              </button>
              <button type="button" className="browse-button esp-fetch-button" onClick={handleFetchFromEsp} disabled={uploadingCsv}>
                {uploadingCsv ? 'Syncing...' : uploadState.type === 'error' ? 'Retry Sync' : uploadState.type === 'success' ? 'Synced - Sync Again' : 'Sync from Datalogger'}
              </button>
            </div>
          </div>
          {uploadingCsv ? <div className="upload-progress"><div style={{ width: `${Math.max(2, uploadState.progress || 0)}%` }} /></div> : null}
          {uploadState.type === 'error' ? (
            <div className="esp-help-inline">Connect your computer to datalogger Wi-Fi, then retry sync.</div>
          ) : null}
        </div>
        <div
          className={`dropzone ${isDragging ? 'dragging' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="dropzone-content">
            <FaUpload className="dropzone-icon" />
            <div className="dropzone-text-block">
              <h3>Drag & Drop Your Files Here</h3>
              <p>Support for CSV, BYB, FIT files, MP4 videos, or both</p>
              <div className="dropzone-actions">
                <div className="dropzone-or">or</div>
                <label className="file-input-label">
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".csv,.CSV,.shq,.SHQ,.byb,.BYB,.fit,.mp4,.mov,.avi,.FIT,.MP4"
                    onChange={handleFileInput}
                    style={{ display: 'none' }}
                  />
                  <button type="button" className="browse-button" onClick={() => fileInputRef.current?.click()}>
                    Browse Files
                  </button>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showEspSettings ? (
        <div className="esp-settings-overlay" onClick={() => setShowEspSettings(false)}>
          <div className="esp-settings-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Datalogger settings</h3>
            <label htmlFor="esp-url-field">Datalogger URL</label>
            <input
              id="esp-url-field"
              type="text"
              className="esp-url-input"
              value={espDraftUrl}
              onChange={(e) => setEspDraftUrl(e.target.value)}
              placeholder="http://192.168.4.1"
            />
            <p className="esp-settings-help">
              Use the device base URL (for example <code className="esp-settings-code">http://192.168.4.1</code>). Sync lists files (API, index page, or common names), then you pick which CSVs to import; each file becomes one run via{' '}
              <code className="esp-settings-code">/download?file=…</code>.
              With <code className="esp-settings-code">npm start</code>, the app calls <code className="esp-settings-code">/datalogger/…</code> on the dev server with a target header so the browser does not need CORS on the logger.
              Production / HTTPS builds need <code className="esp-settings-code">Access-Control-Allow-Origin</code> on the firmware or CSV upload.
            </p>
            <div className="esp-settings-actions">
              <button type="button" className="view-all-button" onClick={() => setShowEspSettings(false)}>Cancel</button>
              <button type="button" className="browse-button" onClick={saveEspSettings}>Save</button>
              <button type="button" className="browse-button esp-fetch-button" onClick={() => { saveEspSettings(); setTimeout(() => { handleFetchFromEsp(); }, 0); }}>
                Save + Fetch now
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {dataloggerPicker.open ? (
        <div
          className={`datalogger-picker-overlay datalogger-picker-overlay--with-sidebar${isCollapsed ? ' datalogger-picker-overlay--sidebar-collapsed' : ''}`}
          onClick={closeDataloggerPicker}
          role="presentation"
        >
          <div
            className="datalogger-picker-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="datalogger-picker-title"
          >
            <h3 id="datalogger-picker-title">Files on datalogger</h3>
            <p className="datalogger-picker-help">
              Choose which CSV files to import as separate runs. With <code className="esp-settings-code">npm start</code>, the app uses the{' '}
              <code className="esp-settings-code">/datalogger</code> dev proxy to reach the device.
            </p>
            <div className="datalogger-picker-toolbar">
              <button type="button" className="view-all-button" onClick={() => setDataloggerAllSelected(true)}>
                Select all
              </button>
              <button type="button" className="view-all-button" onClick={() => setDataloggerAllSelected(false)}>
                Deselect all
              </button>
            </div>
            <ul className="datalogger-picker-list">
              {dataloggerPicker.fileNames.map((name) => (
                <li key={name} className="datalogger-picker-item">
                  <label className="datalogger-picker-label">
                    <input
                      type="checkbox"
                      checked={!!dataloggerPicker.selected[name]}
                      onChange={() => toggleDataloggerFile(name)}
                    />
                    <span className="datalogger-picker-filename">{name}</span>
                  </label>
                </li>
              ))}
            </ul>
            <div className="datalogger-picker-actions">
              <button type="button" className="view-all-button" onClick={closeDataloggerPicker}>
                Cancel
              </button>
              <button
                type="button"
                className="browse-button esp-fetch-button"
                onClick={handleDataloggerPickerImport}
                disabled={!dataloggerPicker.fileNames.some((f) => dataloggerPicker.selected[f])}
              >
                Import selected
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {user && (
        <div className="cloud-logger-section">
          <div className="section-header">
            <h2>Cloud Logger Uploads</h2>
            <button className="view-all-button" type="button" onClick={loadCloudLoggerUploads}>
              Refresh
            </button>
          </div>
          {cloudLoggerUploads.length > 0 ? (
            <div className="cloud-logger-list">
              {cloudLoggerUploads.map((upload) => (
                <div className="cloud-logger-item" key={upload.id}>
                  <div className="cloud-logger-main">
                    <div className="cloud-logger-name">{upload.fileName || upload.id}</div>
                    <div className="cloud-logger-meta">
                      {formatByteSize(Number(upload.size) || 0)}
                      {upload.createdAt ? ` · ${formatUploadDate(upload.createdAt)}` : ''}
                      {upload.status ? ` · ${upload.status}` : ''}
                      {upload.runId ? ' · run created' : ''}
                      {upload.runCreateStatus === 'failed' ? ' · run creation failed' : ''}
                    </div>
                  </div>
                  {upload.runId ? (
                    <button
                      type="button"
                      className="browse-button esp-fetch-button"
                      onClick={() => navigate(`/ride/${upload.runId}`)}
                    >
                      Open run
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="browse-button esp-fetch-button"
                      onClick={() => importCloudLoggerUpload(upload)}
                      disabled={cloudLoggerBusy || !upload.storagePath}
                    >
                      Import
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="cloud-logger-empty">
              Uploaded logger files will appear here after the device upload succeeds.
            </div>
          )}
        </div>
      )}

      {stats.recentRides.length > 0 && (
        <div className="recent-rides-section">
          <div className="section-header">
            <h2>Recent Runs</h2>
            <button className="view-all-button" type="button" onClick={() => navigate('/my-rides')}>
              View All
            </button>
          </div>
          <div className="recent-rides-grid">
            {stats.recentRides.map((ride) => (
              <RunCard
                key={ride.id}
                run={ride}
                viewerUserId={user?.uid}
                onClick={() => navigate(`/ride/${ride.id}`)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
